import React, { useState, useEffect, useRef } from 'react';
import Onboarding from './Onboarding';
import InvestmentMemo from './InvestmentMemo';
import AuthPage from './AuthPage';
import ProductPage from './ProductPage';
import LandingPage from './LandingPage';
import MarketResearch from './MarketResearch';
import Portfolio from './Portfolio';
import WaitlistPage from './WaitlistPage';
import FinancialCalculator from './FinancialCalculator';
import PitchSummarizer from './PitchSummarizer';
import NewsFeed from './NewsFeed';
import FundMemory from './FundMemory';
import TeamPage from './TeamPage';
import AdminPage from './AdminPage';
import { WorkspaceProvider } from './WorkspaceContext';
import PrivacyAndTerms from './PrivacyAndTerms';
import PendingPage from './PendingPage';
import RejectedPage from './RejectedPage';
import { supabase } from './supabaseClient';
import logo from './logo.png';
import {
  LayoutDashboard,
  Calculator,
  Search,
  BarChart3,
  Menu,
  X,
  LogOut,
  ChevronRight,
  CheckCircle2,
  FileSearch,
  Newspaper,
  FileText,
  Lock,
  Brain,
  Users
} from 'lucide-react';

export default function App() {
  const [view, setView] = useState('home');
  const [activeTab, setActiveTab] = useState('Fund Health');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [user, setUser] = useState({ name: "Loading...", role: "" });
  const [userPlan, setUserPlan] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [inactivityToast, setInactivityToast] = useState(false);
  const [calcParams, setCalcParams] = useState({
    cash: 5000000, burn: 250000, revenue: 2000000, growth: 85, margin: 75
  });
  const [apiKeys, setApiKeys] = useState({ tracxn: "", crunchbase: "" });
  const [researchQuery, setResearchQuery] = useState("");
  const [isResearching, setIsResearching] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [deckSummary, setDeckSummary] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedText, setExtractedText] = useState("");

  const lastActivityRef = useRef(Date.now());
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);

  const handleLogin = () => {
    window.history.pushState({}, '', '/dashboard');
    setView('dashboard');
  };

  const applyProfile = (profile, session) => {
    const protectedViews = ['privacy', 'terms', 'admin', 'home', 'product', 'auth', 'pending', 'rejected', 'landing'];
    if (protectedViews.includes(viewRef.current)) return;
    if (!profile) { setView('home'); return; }
    if (profile.status === 'pending') { setView('pending'); return; }
    if (profile.status === 'rejected') { setView('rejected'); return; }
    if (profile.status === 'approved') {
      setView('dashboard');
      setUser({ name: profile.full_name || session.user.email, role: profile.role || "Member" });
      setUserPlan(profile.plan || null);
      if (!profile.onboarded) setShowOnboarding(true);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        supabase.from("profiles").select("*").eq("id", session.user.id).single().then(({ data }) => {
          applyProfile(data, session);
        });
      }
    });

    supabase.auth.onAuthStateChange((_event, session) => {
      if (_event === 'SIGNED_IN' && session) {
        supabase.from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single()
          .then(({ data }) => {
            applyProfile(data, session);
          });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname === '/dashboard') {
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (!session) { setView('home'); window.history.pushState({}, '', '/'); return; }
        const { data: profile } = await supabase.from("profiles").select("*").eq("id", session.user.id).single();
        if (!profile) { setView('home'); return; }
        if (profile.status === 'pending') { setView('pending'); return; }
        if (profile.status === 'rejected') { setView('rejected'); return; }
        if (profile.status === 'approved') {
          setView('dashboard');
          setUser({ name: profile.full_name || session.user.email, role: profile.role || "Member" });
          setUserPlan(profile.plan || null);
          if (!profile.onboarded) setShowOnboarding(true);
          if (window.location.hash && window.location.hash.includes('access_token')) {
            window.history.replaceState(null, '', '/dashboard');
          }
        }
      });
    }
    if (pathname === '/home') setView('home');
    if (pathname === '/waitlist') setView('landing');
    if (pathname === '/product') setView('product');
    if (pathname === '/login') setView('auth');
    if (pathname === '/signup') setView('auth');
    if (pathname === '/binos-admin') setView('admin');
    if (window.location.pathname === '/privacy') setView('privacy');
    if (window.location.pathname === '/terms') setView('terms');
    if (window.location.pathname === '/memory') setActiveTab('memory');
    if (window.location.pathname === '/team') setActiveTab('team');
  }, []);

  // Auto logout after 30 minutes of inactivity
  useEffect(() => {
    const resetTimer = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('click', resetTimer);
    window.addEventListener('keypress', resetTimer);

    const interval = setInterval(async () => {
      const thirtyMinutes = 30 * 60 * 1000;
      if (Date.now() - lastActivityRef.current > thirtyMinutes && viewRef.current === 'dashboard') {
        await supabase.auth.signOut();
        setView('auth');
        setInactivityToast(true);
        setTimeout(() => setInactivityToast(false), 4000);
      }
    }, 60 * 1000);

    return () => {
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('keypress', resetTimer);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setView('home');
  };

  if (view === 'privacy') return <PrivacyAndTerms />;
  if (view === 'terms') return <PrivacyAndTerms />;
  if (view === 'admin') return <AdminPage />;
  if (view === 'home') return <LandingPage setView={setView} />;
  if (view === 'product') return <ProductPage />;
  if (view === 'auth') return <AuthPage onLogin={() => setView('dashboard')} />;
  if (view === 'landing') return <WaitlistPage onEnter={handleLogin} />;
  if (view === 'pending') return <PendingPage />;
  if (view === 'rejected') return <RejectedPage />;

  const NAV_ITEMS = [
    { id: 'Fund Health', icon: LayoutDashboard, plan: null },
    { id: 'Financial Calculator', icon: Calculator, plan: 'Pro' },
    { id: 'Market Research', icon: Search, plan: 'Pro' },
    { id: 'Pitch Summariser', icon: FileSearch, plan: null },
    { id: 'News Feed', icon: Newspaper, plan: null },
    { id: 'Investment Memo', icon: FileText, plan: null },
    { id: 'Reporting', icon: BarChart3, plan: null },
    { id: 'memory', label: 'Fund Memory', icon: Brain, plan: null },
    { id: 'team', label: 'Team', icon: Users, plan: null },
  ];

  const initials = user.name.split(" ").map(n => n[0]).join("").slice(0, 2);

  return (
    <WorkspaceProvider>
    <div style={{ display: "flex", height: "100vh", background: "#0a0a0a", color: "#fff", overflow: "hidden", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;900&display=swap" rel="stylesheet" />

      {/* INACTIVITY TOAST */}
      {inactivityToast && (
        <div style={{ position: "fixed", top: 24, right: 24, zIndex: 1000, background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "14px 20px", boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#fff", margin: 0 }}>Signed out due to inactivity</p>
        </div>
      )}

      {/* UPGRADE MODAL */}
      {showUpgradeModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setShowUpgradeModal(false)}
        >
          <div
            style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "32px", maxWidth: 380, width: "100%" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ width: 44, height: 44, background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
              <Lock style={{ width: 20, height: 20, color: "#dc2626" }} />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: "0 0 8px", letterSpacing: "-0.3px" }}>Pro plan required</h3>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", margin: "0 0 24px", lineHeight: 1.6 }}>
              This feature requires the Pro plan. Contact us to upgrade your account.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <a
                href="mailto:anas@binos-ai.com?subject=Binos%20Pro%20Upgrade"
                style={{ flex: 1, padding: "11px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 800, color: "#fff", cursor: "pointer", textAlign: "center", textDecoration: "none", fontFamily: "inherit" }}
              >
                Contact Us
              </a>
              <button
                onClick={() => setShowUpgradeModal(false)}
                style={{ flex: 1, padding: "11px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit" }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SIDEBAR */}
      <nav style={{
        width: isSidebarOpen ? 240 : 72,
        background: "#0d0d0d",
        borderRight: "1px solid rgba(255,255,255,0.06)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s ease",
        flexShrink: 0,
        zIndex: 50,
      }}>
        {/* Logo */}
        <div style={{ height: 64, display: "flex", alignItems: "center", padding: "0 20px", borderBottom: "1px solid rgba(255,255,255,0.05)", flexShrink: 0, overflow: "hidden" }}>
          <img src={logo} style={{ width: 28, height: 28, flexShrink: 0 }} alt="Binos" />
          {isSidebarOpen && (
            <span style={{ marginLeft: 10, fontWeight: 900, fontSize: 17, color: "#fff", letterSpacing: "-0.5px", whiteSpace: "nowrap" }}>Binos</span>
          )}
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, padding: "16px 10px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto" }}>
          {NAV_ITEMS.map((item) => {
            const isLocked = item.plan && userPlan !== item.plan;
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (isLocked) { setShowUpgradeModal(true); return; }
                  setActiveTab(item.id);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  background: active ? "rgba(220,38,38,0.15)" : "transparent",
                  color: active ? "#fff" : isLocked ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.4)",
                  borderLeft: active ? "2px solid #dc2626" : "2px solid transparent",
                  textAlign: "left",
                }}
              >
                <item.icon style={{ width: 18, height: 18, flexShrink: 0 }} />
                {isSidebarOpen && (
                  <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", letterSpacing: "-0.2px", flex: 1 }}>{item.label || item.id}</span>
                )}
                {isSidebarOpen && isLocked && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: "#dc2626", background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 4, padding: "2px 5px", textTransform: "uppercase", letterSpacing: "0.08em" }}>Pro</span>
                    <Lock style={{ width: 11, height: 11, color: "rgba(255,255,255,0.2)" }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* User + logout */}
        <div style={{ padding: "12px 10px", borderTop: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
          {isSidebarOpen && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 4 }}>
              <div style={{ width: 32, height: 32, background: "#dc2626", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: "#fff", flexShrink: 0 }}>{initials}</div>
              <div style={{ overflow: "hidden" }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.name}</p>
                <p style={{ fontSize: 10, color: "#dc2626", margin: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>{user.role}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, border: "none", cursor: "pointer", background: "transparent", color: "rgba(255,255,255,0.3)", transition: "all 0.15s" }}
          >
            <LogOut style={{ width: 18, height: 18, flexShrink: 0 }} />
            {isSidebarOpen && <span style={{ fontSize: 13, fontWeight: 600 }}>Sign Out</span>}
          </button>
        </div>
      </nav>

      {/* MAIN */}
      <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>

        {/* HEADER */}
        <header style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 32px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "#0d0d0d",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              style={{ padding: 8, background: "rgba(255,255,255,0.05)", borderRadius: 8, border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", display: "flex", transition: "all 0.15s" }}
            >
              {isSidebarOpen ? <X style={{ width: 18, height: 18 }} /> : <Menu style={{ width: 18, height: 18 }} />}
            </button>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: "#fff", margin: 0, letterSpacing: "-0.5px" }}>{activeTab}</h1>
              <p style={{ fontSize: 9, fontWeight: 700, color: "#dc2626", margin: 0, textTransform: "uppercase", letterSpacing: "0.15em" }}>Intelligence Terminal</p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ textAlign: "right" }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#fff", margin: 0 }}>{user.name}</p>
              <p style={{ fontSize: 9, fontWeight: 700, color: "#dc2626", margin: 0, textTransform: "uppercase", letterSpacing: "0.1em" }}>{user.role}</p>
            </div>
            <div style={{ width: 36, height: 36, background: "#dc2626", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#fff" }}>{initials}</div>
          </div>
        </header>

        {/* CONTENT */}
        <div style={{ flex: 1, overflowY: "auto", padding: 32, background: "#111" }}>
          <TabContent
            activeTab={activeTab}
            calcParams={calcParams} setCalcParams={setCalcParams}
            apiKeys={apiKeys} setApiKeys={setApiKeys}
            researchQuery={researchQuery} setResearchQuery={setResearchQuery}
            isResearching={isResearching} setIsResearching={setIsResearching}
            isSummarizing={isSummarizing} setIsSummarizing={setIsSummarizing}
            deckSummary={deckSummary} setDeckSummary={setDeckSummary}
            uploadedFile={uploadedFile} setUploadedFile={setUploadedFile}
            extractedText={extractedText} setExtractedText={setExtractedText}
          />
        </div>

        {showOnboarding && <Onboarding user={user} onComplete={() => setShowOnboarding(false)} />}
      </main>
    </div>
    </WorkspaceProvider>
  );
}

function TabContent(props) {
  switch (props.activeTab) {
    case 'Fund Health': return <Portfolio />;
    case 'Financial Calculator': return <FinancialCalculator {...props} />;
    case 'Market Research': return <MarketResearch apiKeys={props.apiKeys} setApiKeys={props.setApiKeys} />;
    case 'Pitch Summariser': return <PitchSummarizer {...props} />;
    case 'News Feed': return <NewsFeed />;
    case 'Admin': return <AdminPage />;
    case 'Investment Memo': return <InvestmentMemo />;
    case 'Reporting': return <Reporting />;
    case 'memory': return <FundMemory />;
    case 'team': return <TeamPage />;
    default: return null;
  }
}

function Reporting() {
  const reports = [
    { name: 'NexaFlow', status: 'Submitted', date: 'Oct 12', summary: 'Hit $1M ARR milestone. Expanding to Japan.' },
    { name: 'MediGen', status: 'Submitted', date: 'Oct 10', summary: 'FDA Phase II expected Q1. Partnership with Merck.' },
    { name: 'EcoGrid', status: 'Overdue', date: 'Oct 01', summary: 'Founder unresponsive. High churn risk.' }
  ];

  return (
    <div className="animate-in fade-in duration-500">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 900, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" }}>Portfolio Narrative</h2>
          <p style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)", margin: 0, textTransform: "uppercase", letterSpacing: "0.1em" }}>Status of 24 Active Entities</p>
        </div>
        <button style={{ padding: "10px 20px", background: "#dc2626", color: "#fff", fontWeight: 800, borderRadius: 10, fontSize: 11, border: "none", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.1em" }}>
          Initiate Q1 Collection
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        <ReportSummaryCard label="LP Narrative" status="Drafting" progress={65} color="#dc2626" />
        <ReportSummaryCard label="Tax/K-1 Status" status="Verified" progress={100} color="#16a34a" />
        <ReportSummaryCard label="CEO Updates" status="Pending (3)" progress={85} color="#d97706" />
      </div>

      <div style={{ background: "#1a1a1a", borderRadius: 16, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.03)" }}>
              {["Company", "Submission", "Highlight", "Review"].map((h, i) => (
                <th key={i} style={{ padding: "14px 24px", textAlign: i === 3 ? "right" : "left", fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.12em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reports.map((r, i) => (
              <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                <td style={{ padding: "18px 24px", fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: "-0.3px" }}>{r.name}</td>
                <td style={{ padding: "18px 24px" }}>
                  <span style={{ padding: "3px 10px", borderRadius: 100, fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", background: r.status === 'Submitted' ? "rgba(22,163,74,0.15)" : "rgba(220,38,38,0.15)", color: r.status === 'Submitted' ? "#16a34a" : "#dc2626" }}>
                    {r.status}
                  </span>
                </td>
                <td style={{ padding: "18px 24px", fontSize: 12, color: "rgba(255,255,255,0.4)", fontWeight: 500, maxWidth: 240 }}>{r.summary}</td>
                <td style={{ padding: "18px 24px", textAlign: "right" }}>
                  <button style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 800, color: "#dc2626", background: "none", border: "none", cursor: "pointer" }}>
                    View Details <ChevronRight style={{ width: 13, height: 13 }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportSummaryCard({ label, status, progress, color }) {
  return (
    <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <p style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>{label}</p>
        <CheckCircle2 style={{ width: 16, height: 16, color: progress === 100 ? "#16a34a" : "rgba(255,255,255,0.1)" }} />
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 900, color: "#fff", margin: "0 0 16px", letterSpacing: "-0.5px" }}>{status}</h3>
      <div style={{ width: "100%", height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 100, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${progress}%`, background: color, borderRadius: 100, transition: "width 0.7s ease" }} />
      </div>
    </div>
  );
}
